import { useEffect, useState } from 'react';
import { api, ApiError, type StudioSettings } from '../lib/api.ts';

interface SettingsEditorProps {
  token: string;
  onUnauthorised: () => void;
}

interface Field {
  key: keyof StudioSettings;
  label: string;
  unit: string;
  min: number;
  max: number;
  step: number;
  help: string;
}

const FIELDS: Field[] = [
  {
    key: 'bufferBeforeMinutes',
    label: 'Time before each appointment',
    unit: 'minutes',
    min: 0,
    max: 240,
    step: 5,
    help: 'For the consultation, and for asking clients to arrive a few minutes early.',
  },
  {
    key: 'bufferAfterMinutes',
    label: 'Time after each appointment',
    unit: 'minutes',
    min: 0,
    max: 240,
    step: 5,
    help: 'For taking payment and cleaning the room before the next client.',
  },
  {
    key: 'slotGranularityMinutes',
    label: 'Offer start times every',
    unit: 'minutes',
    min: 5,
    max: 120,
    step: 5,
    help: 'How the times in the booking picker are spaced. 15 gives 09:00, 09:15, 09:30…',
  },
  {
    key: 'minLeadTimeMinutes',
    label: 'Minimum notice',
    unit: 'minutes',
    min: 0,
    max: 20_160,
    step: 30,
    help: 'How far ahead a client must book. 120 means nothing within the next two hours.',
  },
  {
    key: 'maxDaysAhead',
    label: 'Diary open for',
    unit: 'days',
    min: 1,
    max: 365,
    step: 7,
    help: 'How far into the future clients can see and book.',
  },
  {
    key: 'holdMinutes',
    label: 'Hold an unpaid booking for',
    unit: 'minutes',
    min: 5,
    max: 120,
    step: 5,
    help: 'How long a slot stays reserved while someone is paying. Released automatically after.',
  },
  {
    key: 'depositPercent',
    label: 'Deposit',
    unit: '% of the price',
    min: 0,
    max: 100,
    step: 5,
    help: 'Taken at booking. The balance is paid on the day.',
  },
  {
    key: 'cancellationWindowHours',
    label: 'Free cancellation up to',
    unit: 'hours before',
    min: 0,
    max: 720,
    step: 12,
    help: 'Cancel with at least this much notice and the deposit is refunded automatically.',
  },
];

export function SettingsEditor({ token, onUnauthorised }: SettingsEditorProps) {
  const [saved, setSaved] = useState<StudioSettings | null>(null);
  const [draft, setDraft] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);

  useEffect(() => {
    api
      .admin.getSettings(token)
      .then((result) => {
        setSaved(result);
        setDraft(Object.fromEntries(Object.entries(result).map(([k, v]) => [k, String(v)])));
      })
      .catch((caught: unknown) => {
        if (caught instanceof ApiError && caught.status === 401) return onUnauthorised();
        setError('Settings could not be loaded.');
      });
  }, [token, onUnauthorised]);

  const invalid = FIELDS.filter((field) => {
    const value = Number(draft[field.key]);
    return !Number.isInteger(value) || value < field.min || value > field.max;
  });

  const dirty =
    saved !== null &&
    FIELDS.some((field) => Number(draft[field.key]) !== (saved[field.key] as number));

  async function save() {
    if (!saved) return;
    setBusy(true);
    setError(null);
    try {
      const changes: Partial<StudioSettings> = {};
      for (const field of FIELDS) {
        const value = Number(draft[field.key]);
        if (value !== saved[field.key]) {
          (changes as Record<string, number>)[field.key] = value;
        }
      }
      const result = await api.admin.saveSettings(token, changes);
      setSaved(result);
      setDraft(Object.fromEntries(Object.entries(result).map(([k, v]) => [k, String(v)])));
      setMessage('Settings saved. Availability is recalculated from now on.');
    } catch (caught) {
      if (caught instanceof ApiError && caught.status === 401) return onUnauthorised();
      setError(caught instanceof ApiError ? caught.message : 'Settings could not be saved.');
    } finally {
      setBusy(false);
    }
  }

  if (!saved) {
    return (
      <section className="page">
        {error ? (
          <p className="notice notice--error">{error}</p>
        ) : (
          <p className="booking__loading">
            <span className="spinner" /> Loading settings…
          </p>
        )}
      </section>
    );
  }

  const before = Number(draft.bufferBeforeMinutes) || 0;
  const after = Number(draft.bufferAfterMinutes) || 0;

  return (
    <section className="page stack-lg">
      <div>
        <h2 className="admin__section-title">Booking rules</h2>
        <p className="field-hint">
          These control what the booking page offers. Existing appointments are never moved or
          cancelled by a change here.
        </p>
      </div>

      {/*
        The two buffers only ever act together on spacing, so saying so plainly
        avoids the studio wondering why 5+20 and 0+25 behave identically.
      */}
      <p className="notice">
        With {before} minutes before and {after} after, <strong>{before + after} minutes</strong> will
        always separate the end of one appointment from the start of the next.
      </p>

      <div className="settings-grid">
        {FIELDS.map((field) => {
          const value = draft[field.key] ?? '';
          const bad = invalid.includes(field);
          return (
            <div key={field.key} className="field settings-field">
              <label htmlFor={`setting-${field.key}`}>{field.label}</label>
              <div className="settings-field__input">
                <input
                  id={`setting-${field.key}`}
                  className="input input--compact"
                  type="number"
                  min={field.min}
                  max={field.max}
                  step={field.step}
                  value={value}
                  aria-invalid={bad}
                  onChange={(event) => {
                    setDraft((current) => ({ ...current, [field.key]: event.target.value }));
                    setMessage(null);
                  }}
                />
                <span className="settings-field__unit">{field.unit}</span>
              </div>
              <p className="field-hint">{field.help}</p>
              {bad && (
                <p className="field-error">
                  Must be a whole number between {field.min} and {field.max}.
                </p>
              )}
            </div>
          );
        })}
      </div>

      {message && <p className="notice notice--success">{message}</p>}
      {error && <p className="notice notice--error">{error}</p>}

      <div className="row">
        <button
          type="button"
          className="btn"
          onClick={save}
          disabled={busy || !dirty || invalid.length > 0}
        >
          {busy ? <span className="spinner" /> : 'Save settings'}
        </button>
        {dirty && (
          <button
            type="button"
            className="btn btn--quiet"
            onClick={() =>
              setDraft(Object.fromEntries(Object.entries(saved).map(([k, v]) => [k, String(v)])))
            }
          >
            Discard changes
          </button>
        )}
      </div>
    </section>
  );
}
